import type { CollectionViewsService } from './CollectionViewsService'
import type { ObjectsApiService } from './ObjectsApiService'
import { logError } from '@/common/errors/log-utils'
import { BehaviorSubject, filter, firstValueFrom, Subject, type Subscription } from 'rxjs'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService'
import { isClientId } from '@/common/network/gql-types'
import type { CreateObjectInput } from '@/generated/graphql.ts'
import type { OperationResult } from '@/common/errors/OperationResult.ts'
import type { ObjectModel, ObjectTypeModel } from '@/core/gql/gql-models-override.ts'
import type { AuthService } from '@/core/auth/services/AuthService.ts'
import * as op from '../../common/errors/OperationResult'
import type { PropertyValue } from '@/objects/gql/PropertyValue.ts'
import { TypeChangeKind, type TypeChangeEvent } from '@/object-types/services/ObjectTypesService.ts'

export type { PropertyValue } from '@/objects/gql/PropertyValue.ts'

export interface ObjectUpdatedEvent {
  typeId: number
  prev: ObjectModel | undefined
  updated: ObjectModel | undefined
}

export class ObjectsService {
  private fetchAbortController?: AbortController
  // private typesFetchedWaitAbortCtrl?: ObservableAbortController
  private typeIdToObjects = new Map<number, BehaviorSubject<ObjectModel[] | undefined>>()
  private typesSubscription: Subscription | null = null
  private prevTypeIds = new Set<number>()

  private readonly _objectUpdated$ = new Subject<ObjectUpdatedEvent>()
  public readonly objectUpdated$ = this._objectUpdated$.asObservable()

  constructor(
    private readonly apiService: ObjectsApiService,
    private readonly typesService: ObjectTypesService,
    private readonly collectionViewsService: CollectionViewsService,
    private readonly authService: AuthService,
  ) {
    this.authService.onAuthStatusChange$.subscribe(this.onAuthStatusChanged.bind(this))
    this.typesService.typeChanged$.subscribe(this.onTypeChange.bind(this))
  }

  public async createObject(input: CreateObjectInput): Promise<OperationResult<ObjectModel>> {
    if (input.propertyValues.some((x) => x.value === 'err')) {
      return op.error('Create error')
    }
    const result = await this.apiService.create(input)
    if (result.isSuccess()) {
      const newObject = result.result!
      const subj = this.getObjectsOfTypeSubject(newObject.typeId)
      subj.value?.push(newObject)
      this._objectUpdated$.next({ typeId: newObject.typeId, prev: undefined, updated: newObject })
    }
    return result
  }

  public async updateObjectProperty(
    obj: ObjectModel,
    propDefId: number,
    value: PropertyValue,
  ): Promise<OperationResult> {
    const updatedPropertyValues = new Map(obj.propertyValues)
    updatedPropertyValues.set(propDefId, value)
    const updated: ObjectModel = { ...obj, propertyValues: updatedPropertyValues }

    const typeId = obj.typeId
    const arr = this.typeIdToObjects.get(typeId)!.value!
    arr[arr.findIndex((o) => o.id === obj.id)] = updated

    this._objectUpdated$.next({ typeId, prev: obj, updated })

    if (isClientId(obj.id)) {
      const message = 'trying to update local-id obj: '
      logError(message, { id: obj.id })
      return op.error(message)
    }

    const { error } = await this.apiService.update({
      id: obj.id,
      propertyValues: [
        { definitionId: propDefId, value: this.apiService.propertyValueToString(value) },
      ],
    })
    if (error) {
      logError(`failed to update object ${obj.id} property ${propDefId}: ${error.message}`)
      return op.error(error)
    }

    return op.success()
  }

  public objectsOfType$(typeId: number) {
    return this.getObjectsOfTypeSubject(typeId)
  }

  public findObjectById(objectId: number): ObjectModel | undefined {
    for (const subj of this.typeIdToObjects.values()) {
      const found = subj.value?.find((o) => o.id === objectId)
      if (found) return found
    }
    return undefined
  }

  public findObjectByIdOfType(objectId: number, typeId: number): ObjectModel | undefined {
    return this.typeIdToObjects.get(typeId)?.value?.find((o) => o.id === objectId)
  }

  private onAuthStatusChanged(isAuthed: boolean): void {
    if (!isAuthed) {
      return
    }
    void this.init()
  }

  private async init(): Promise<void> {
    await this.fetchLastOpenedCollectionObjects()
    this.fetchObjectsForSelectedSpace()
  }

  private async fetchLastOpenedCollectionObjects(): Promise<void> {
    const lastOpenedCollectionTypeId =
      this.collectionViewsService.localStorageState?.lastOpenedCollectionTypeId

    if (!lastOpenedCollectionTypeId) {
      return
    }

    const types = await firstValueFrom(this.typesService.selectedSpaceTypes$.pipe(filter(Boolean)))

    if (!types.some((t) => t.id === lastOpenedCollectionTypeId)) {
      console.log(
        `[ObjectsService] lastOpenedCollectionTypeId ${lastOpenedCollectionTypeId} not found in fetched types — skipping prefetch`,
      )
      return
    }

    await this.fetchObjectsOfTypes([lastOpenedCollectionTypeId], false)
  }

  private fetchObjectsForSelectedSpace(): void {
    if (this.typesSubscription) {
      return
    }
    this.typesSubscription = this.typesService.selectedSpaceTypes$.subscribe(
      this.onSelectedSpaceTypesFetched.bind(this),
    )
  }

  private onSelectedSpaceTypesFetched(types: ObjectTypeModel[] | undefined): void {
    if (!types || types.length === 0) {
      this.prevTypeIds = new Set()
      return
    }

    const newTypeIds = types.map((x) => x.id).filter((id) => !this.prevTypeIds.has(id))
    this.prevTypeIds = new Set(types.map((x) => x.id))

    if (newTypeIds.length === 0) return

    const lastOpenedCollectionTypeId =
      this.collectionViewsService.localStorageState?.lastOpenedCollectionTypeId

    const toFetch = lastOpenedCollectionTypeId
      ? newTypeIds.filter((id) => id !== lastOpenedCollectionTypeId)
      : newTypeIds

    if (toFetch.length === 0) return
    void this.fetchObjectsOfTypes(toFetch)
  }

  private onTypeChange(events: TypeChangeEvent[]): void {
    const typeId = events[0].typeId
    if (events.some((e) => e.typeId !== typeId))
      console.error('onTypeChange: mixed typeIds in batch', events)

    for (const event of events) {
      if (event.kind === TypeChangeKind.TypeDeleted) {
        this.typeIdToObjects.delete(typeId)
        this.prevTypeIds.delete(typeId)
        return
      }
    }

    this.updateAndReemit(typeId, (values) => {
      for (const event of events) {
        if (event.kind === TypeChangeKind.PropDefAdded) values.set(event.propDef.id, null)
        if (event.kind === TypeChangeKind.PropDefDeleted) values.delete(event.propDefId)
      }
    })
  }

  private updateAndReemit(typeId: number, updateValues: (values: Map<number, PropertyValue>) => void): void {
    const subj = this.typeIdToObjects.get(typeId)
    if (!subj?.value) return
    subj.next(subj.value.map((obj) => {
      const propertyValues = new Map(obj.propertyValues)
      updateValues(propertyValues)
      return { ...obj, propertyValues }
    }))
  }

  private getObjectsOfTypeSubject(typeId: number): BehaviorSubject<ObjectModel[] | undefined> {
    if (!this.typeIdToObjects.has(typeId)) {
      this.typeIdToObjects.set(typeId, new BehaviorSubject<ObjectModel[] | undefined>(undefined))
    }

    return this.typeIdToObjects.get(typeId)!
  }

  private async fetchObjectsOfTypes(typeIds: number[], abortable: boolean = true) {
    this.fetchAbortController?.abort()
    let signal: AbortSignal | undefined = undefined
    if (abortable) {
      this.fetchAbortController = new AbortController()
      signal = this.fetchAbortController.signal
    }

    const { error, result } = await this.apiService.objectsOfTypes(typeIds, signal)

    if (error) {
      logError(`error fetching objects for types: ${error.message}`, { typeIds })
      return
    }

    for (const [typeIdStr, objects] of Object.entries(result!)) {
      const objectsSubject = this.getObjectsOfTypeSubject(Number(typeIdStr))
      objectsSubject.next(objects)
    }
  }
}
